import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AsgardFeatureNames } from 'src/models/AsgardFeatures';

declare global {
  interface Window {
    gtag?: (
      command: string,
      eventName: string,
      params?: Record<string, unknown>
    ) => void;
  }
}

export enum GoogleAnalyticsActions {
  SEARCH = 'search_action',
  READING_SPEED = 'reading_speed_changed',
  READING_SPEED_SETTINGS = 'reading_speed_settings_changed',
  VOICE = 'voice_changed',
  DRAWING_TOOLS = 'drawing_tool_changed',
  NOTE_CREATED = 'note_created',
  HIGHLIGHT_ADDED = 'highlight_added',
  READING_MODE = 'reading_mode_changed',
  READING_UNIT = 'reading_unit_changed',
  TYPING_UNIT = 'typing_unit_changed'
}

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {
  public readonly featuresToTrack: AsgardFeatureNames[] = [
    AsgardFeatureNames.AUDIO_MAKER,
    AsgardFeatureNames.SCREENSHOT_READER,
    AsgardFeatureNames.LOOK_UP,
    AsgardFeatureNames.TRANSLATE,
    AsgardFeatureNames.IMAGE_DICTIONARY,
    AsgardFeatureNames.SEARCH,
    AsgardFeatureNames.SPELL_CHECK,
    AsgardFeatureNames.WORD_PREDICTION,
  ];

  trackEvent(eventName: string, params?: Record<string, unknown>): void {
    // NOTE 9 JUN 2026
    // we'll only log events for Develop at the moment
    // not for staging and production
    if (!environment.production) {
      window.gtag?.('event', eventName, params);
    }
  }

  trackFeature(feature: AsgardFeatureNames): void {
    const name = this.convertFeatureNameToEventName(feature);
    this.trackEvent('feature_used', { feature_name: name });
  }

  trackAction(name: GoogleAnalyticsActions, value: string = ''): void {
    this.trackEvent('feature_used', { feature_name: name + (value ? `_${value}` : '') });
  }

  private convertFeatureNameToEventName(feature: AsgardFeatureNames): string {
    switch (feature) {
      case AsgardFeatureNames.AUDIO_MAKER:
        return 'audio_maker_used';
      case AsgardFeatureNames.SCREENSHOT_READER:
        return 'screenshot_reader_used';
      case AsgardFeatureNames.LOOK_UP:
        return 'look_up_tools_used';
      case AsgardFeatureNames.TRANSLATE:
        return 'translate_used';
      case AsgardFeatureNames.IMAGE_DICTIONARY:
        return 'image_dictionary_used';
      case AsgardFeatureNames.SEARCH:
        return 'search_used';
      case AsgardFeatureNames.SPELL_CHECK:
        return 'spell_check_used';
      case AsgardFeatureNames.WORD_PREDICTION:
        return 'word_prediction_used';
      default:
        return feature;
    }
  }
}
